import { useState } from 'react';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { bookEditionsInfiniteQueryOptions } from '../api/product.api';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { getBookEditionColumns } from '../components/CatalogueColumns';
import { BookEditionFormModal } from '../components/CatalogueFormModal';
import { useCatalogueNameSearch } from '../hooks/useCatalogueNameSearch';
import type { BookEdition } from '../types/product.types';
import { getBookEditionStatCards } from '../utils/productStatCards';

export function BookEditionPage() {
  const controller = useCatalogueNameSearch('bookEditions');
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(bookEditionsInfiniteQueryOptions(controller.search));

  const [editing, setEditing] = useState<BookEdition | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);

  const columns = getBookEditionColumns(controller.search.search, {
    onEdit: (row) => {
      setEditing(row);
      setFormOpen(true);
    },
  });

  return (
    <CatalogueListLayout
      title="Book Edition"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search editions',
      }}
      stats={{
        cards: getBookEditionStatCards(firstPageMeta),
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
      onAdd={() => {
        setEditing(null);
        setFormOpen(true);
      }}
      addLabel="Add Book Edition"
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the editions"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No edition matches"
          {...loadMoreProps}
        />
      )}

      <BookEditionFormModal bookEdition={editing} open={isFormOpen} onOpenChange={setFormOpen} />
    </CatalogueListLayout>
  );
}
